import type { ReactNode } from "react";
import { useEffect, useId, useState } from "react";
import { Pin, PinOff } from "lucide-react";
import { cn } from "../utils/cn";
import { IconButton } from "../primitives/IconButton";

type CollapsibleSidebarProps = {
  children: ReactNode | ((options: { isExpanded: boolean }) => ReactNode);
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  pinned: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onTogglePinned: () => void;
  allowHoverOpen?: boolean;
  isMobile?: boolean;
  title?: string;
  headerIcon?: string;
  headerMeta?: ReactNode;
  allowHeaderToggle?: boolean;
  /** Override default pinned width (e.g. 220 for a narrower inventory menu) */
  pinnedWidth?: number;
};

const PINNED_WIDTH = 360;
const COLLAPSED_WIDTH = 64;

export function CollapsibleSidebar({
  children,
  isOpen,
  onOpenChange,
  pinned,
  collapsed,
  onToggleCollapsed,
  onTogglePinned,
  allowHoverOpen = false,
  isMobile = false,
  title = "Navigation",
  headerIcon,
  headerMeta,
  allowHeaderToggle = true,
  pinnedWidth,
}: CollapsibleSidebarProps) {
  const [isHovered, setIsHovered] = useState(false);
  const contentId = useId();

  const shouldExpand = pinned || !collapsed || (allowHoverOpen && isHovered);
  const effectivePinnedWidth = pinnedWidth ?? PINNED_WIDTH;
  const sidebarWidth = shouldExpand ? effectivePinnedWidth : COLLAPSED_WIDTH;

  useEffect(() => {
    if (!isMobile || !isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange?.(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile, isOpen, onOpenChange]);

  if (isMobile) {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 z-20">
        <button
          type="button"
          className="absolute inset-0 bg-bg/80"
          aria-label="Fermer le menu"
          onClick={() => onOpenChange?.(false)}
        />
        <aside className="relative h-full w-[320px] max-w-[85vw] border-r border-border bg-surface shadow-md transition-transform duration-150 ease-out">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-xs text-muted">{title}</span>
            <IconButton
              icon="✕"
              ariaLabel="Fermer le menu"
              intent="ghost"
              size="sm"
              onClick={() => onOpenChange?.(false)}
            />
          </div>
          <div className="px-3 py-3">{children}</div>
        </aside>
      </div>
    );
  }

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 flex-col rounded-md border border-border bg-surface-subtle shadow-sm overflow-hidden transition-[width] duration-150 ease-out sidebar-gradient"
      )}
      style={{ width: sidebarWidth }}
      onMouseEnter={() => {
        if (!pinned) setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (!pinned) setIsHovered(false);
      }}
    >
      <div className="flex items-center gap-2 border-b border-border px-2 py-2">
        <IconButton
          icon={pinned ? <Pin size={14} strokeWidth={1.5} /> : <PinOff size={14} strokeWidth={1.5} />}
          ariaLabel="Épingler le panneau"
          intent="ghost"
          size="sm"
          onClick={onTogglePinned}
        />
        <button
          type="button"
          className="flex flex-1 items-center justify-between gap-2 text-left text-xs text-muted"
          aria-expanded={shouldExpand}
          aria-controls={contentId}
          onClick={() => {
            if (!allowHeaderToggle) return;
            onToggleCollapsed();
          }}
        >
          <span className="flex items-center gap-2">
            {headerIcon ? <span className="text-sm text-text">{headerIcon}</span> : null}
            <span className="whitespace-nowrap">{shouldExpand ? title : ""}</span>
          </span>
          {shouldExpand && headerMeta ? <span className="text-xs text-muted">{headerMeta}</span> : null}
        </button>
      </div>
      <div id={contentId} className={cn("flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-3", !pinned && !isHovered && "opacity-80")}>
        {typeof children === "function" ? children({ isExpanded: shouldExpand }) : children}
      </div>
    </aside>
  );
}
